import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown } from "lucide-react";
import PortraitPlaceholder, { EditorialImage } from "@/components/Placeholder";
import { PullQuotePrimary, PullQuoteInline, PullQuoteSupporting } from "@/components/PullQuote";
import EmailSignup from "@/components/EmailSignup";
import { QUOTES } from "@/lib/quotes";
import { TID } from "@/constants/testIds";
import { useSEO, SEO } from "@/lib/seo";

// Clean authentic-photo frame — used where labelled placeholders previously sat.
// Keeps the same aspect locks; one src + alt + focal-position change replaces it later.
function PortraitPhoto({ src, alt, aspect = "aspect-[4/5]", position = "center" }) {
    return (
        <figure className={`relative overflow-hidden ${aspect} bg-charcoal-soft`}>
            <img
                src={src}
                alt={alt}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ objectPosition: position }}
            />
        </figure>
    );
}

const IMG = {
    stage: "/images/stage.jpg",
    curtain: "/images/curtain.jpg",
    warmShadow: "/images/warm-shadow.jpg",
    microphone: "/images/microphone.jpg",
    soap: "/images/alati-soap.jpg",
    hero: "/images/chrissa-hero.jpg",
    father: "/images/chrissa-father.jpg",
};

export default function Home() {
    useSEO(SEO.home);
    return (
        <main>
            {/* ───────────────────────────── HERO ───────────────────────────── */}
            <section className="relative bg-charcoal text-ivory overflow-hidden">
                {/* Background stage lighting texture */}
                <div className="absolute inset-0 opacity-30">
                    <img
                        src={IMG.stage}
                        alt=""
                        aria-hidden="true"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/90 to-charcoal/40" />
                </div>
                <div className="absolute inset-0 grain opacity-20 pointer-events-none" />

                <div className="container-editorial relative pt-36 md:pt-44 pb-24 md:pb-32">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                        <div className="lg:col-span-7 fade-up">
                            <p className="eyebrow text-gold/90 mb-8">Chrissa Valanti</p>
                            <h1 className="display-1 text-ivory">
                                My life began<br />
                                <span className="italic text-gold/95">when they told me</span><br />
                                it was over.
                            </h1>

                            <div className="mt-12 max-w-xl space-y-5 body-lead text-ivory/80 fade-up fade-up-delay-1">
                                <p>
                                    I'm Chrissa Valanti — a singer, mother, founder and two-time blood cancer survivor.
                                </p>
                                <p>
                                    I know what it feels like to survive something enormous and still wonder whether the
                                    person you used to be is ever coming back.
                                </p>
                                <p className="text-ivory/95">
                                    My journey is no longer only about what happened to me.
                                    It is about what I am choosing to build from it.
                                </p>
                            </div>

                            <div className="mt-12 flex flex-wrap gap-4 fade-up fade-up-delay-2">
                                <Link to="/journey" data-testid={TID.home.heroBegin} className="btn-primary">
                                    Begin My Journey <ArrowRight size={16} strokeWidth={1.5} />
                                </Link>
                                <Link to="/building" data-testid={TID.home.heroBuilding} className="btn-secondary-light">
                                    See What I'm Building
                                </Link>
                            </div>
                        </div>

                        <div className="lg:col-span-5 fade-up fade-up-delay-3">
                            <div className="relative">
                                <PortraitPhoto
                                    src={IMG.hero}
                                    alt="Chrissa Valanti holding a microphone at a professional speaking event."
                                    aspect="aspect-[4/5]"
                                    position="center 25%"
                                />
                                <div className="absolute -bottom-6 -left-6 hidden md:block">
                                    <p className="font-serif italic text-gold/90 text-base bg-charcoal px-4 py-2 border border-gold/30">
                                        Begin from where you are.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="hidden md:flex items-center gap-3 absolute bottom-8 left-1/2 -translate-x-1/2 text-ivory/50">
                        <span className="eyebrow">Scroll</span>
                        <ArrowDown size={14} strokeWidth={1.5} />
                    </div>
                </div>
            </section>

            {/* ───────────────────── AUDIENCE MIRROR (light) ───────────────────── */}
            <section className="bg-ivory text-charcoal">
                <div className="container-editorial py-24 md:py-36">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                        <div className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start">
                            <p className="eyebrow text-oxblood">Section 02 — The Mirror</p>
                            <h2 className="display-2 mt-6">
                                Surviving and feeling alive are not always the same thing.
                            </h2>
                            <div className="mt-10 rule" />
                        </div>

                        <div className="lg:col-span-7 space-y-6 body-lead text-charcoal/80">
                            <p>Have you ever made it through something difficult, only to discover that part of you was still missing?</p>
                            <p>Perhaps your body changed.</p>
                            <p>Perhaps your confidence disappeared.</p>
                            <p>Perhaps you stopped doing the thing that once made you feel most like yourself.</p>
                            <p className="text-charcoal">
                                That was music for me. After my diagnosis, I stepped away from a career I had loved for
                                decades.
                            </p>
                            <p>Cancer gave me a reason to hide, and for a long time, I used it.</p>
                            <p>I thought surviving should have been enough.</p>
                            <p className="font-serif italic text-3xl md:text-4xl text-oxblood leading-tight pt-4">
                                It wasn't.
                            </p>

                            <div className="pt-12">
                                <PullQuoteInline>{QUOTES.bodyWorth}</PullQuoteInline>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ────────────────────── TURNING POINT (dark) ────────────────────── */}
            <section className="relative bg-charcoal text-ivory overflow-hidden">
                <div className="absolute inset-0 opacity-25">
                    <img src={IMG.curtain} alt="" aria-hidden="true" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-b from-charcoal/90 via-charcoal/70 to-charcoal" />
                </div>

                <div className="container-editorial relative py-24 md:py-40">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                        <div className="lg:col-span-5 order-2 lg:order-1">
                            <PortraitPhoto
                                src={IMG.father}
                                alt="Chrissa Valanti at home with her father, Antonios Kathopoulis."
                                aspect="aspect-[3/4]"
                                position="center"
                            />
                        </div>
                        <div className="lg:col-span-7 order-1 lg:order-2">
                            <p className="eyebrow text-gold/90">Section 03 — Turning Point</p>
                            <h2 className="display-2 mt-6 text-ivory">
                                My father reminded me <span className="italic text-gold">who I was.</span>
                            </h2>

                            <div className="mt-10 space-y-5 body-lead text-ivory/80 max-w-2xl">
                                <p>
                                    While I was in hospital undergoing my second stem-cell transplant, my father died.
                                </p>
                                <p>
                                    Among his final words to me was a reminder of how much he loved hearing me sing.
                                    His wish was simple. He wanted me to find my voice again.
                                </p>
                                <p>I could not honour that wish by continuing to disappear.</p>
                                <p className="text-ivory">So I returned to the stage.</p>
                                <p>I was frightened. I was changed. I did not feel completely ready.</p>
                                <p className="text-ivory">But when I began to sing, something inside me returned.</p>
                            </div>

                            <div className="mt-12 pl-6 border-l-2 border-gold">
                                <p className="font-serif italic text-2xl md:text-3xl leading-snug text-ivory">
                                    {QUOTES.survivalIdentity}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ───────────────────── CORE BELIEF (light) ───────────────────── */}
            <section className="bg-ivory text-charcoal">
                <div className="container-editorial py-24 md:py-36">
                    <div className="max-w-3xl">
                        <p className="eyebrow text-oxblood">Section 04 — Core Belief</p>
                        <h2 className="display-2 mt-6">
                            What tried to end my story became <span className="italic">the beginning of another one.</span>
                        </h2>
                    </div>

                    <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                        <div className="lg:col-span-7 space-y-5 body-lead text-charcoal/80">
                            <p>I once believed illness had removed me from the life I wanted.</p>
                            <p className="text-charcoal">Now I believe something very different.</p>
                            <p>Adversity may change your body, your plans and your timeline.</p>
                            <p>It does not get to decide your worth.</p>
                            <p>It does not get to take your identity.</p>
                            <p className="text-charcoal">
                                And it does not automatically disqualify you from beginning again.
                            </p>
                        </div>

                        <div className="lg:col-span-5 space-y-8">
                            <EditorialImage
                                src={IMG.warmShadow}
                                alt="Warm architectural shadows — editorial texture"
                                aspect="aspect-[4/3]"
                                overlay="dark"
                            />
                            <PullQuoteSupporting label="Featured">
                                {QUOTES.adversity}
                            </PullQuoteSupporting>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pull quote primary (between sections) */}
            <PullQuotePrimary>{QUOTES.cancerBeginning}</PullQuotePrimary>

            {/* ───────────────────── WHAT I'M BUILDING ───────────────────── */}
            <section className="bg-charcoal text-ivory">
                <div className="container-editorial py-24 md:py-36">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <p className="eyebrow text-gold/90">Section 05 — What I'm Building</p>
                            <h2 className="display-2 mt-6 text-ivory">
                                I am turning <span className="italic text-gold/95">lived experience</span> into something useful.
                            </h2>
                        </div>
                        <div className="lg:col-span-7 lg:pt-4 space-y-5 body-base text-ivory/75">
                            <p>My journey has led me into places I never expected to go.</p>
                            <p>I never expected to build a technology company.</p>
                            <p>I never expected to create a soap brand with my son.</p>
                            <p>I never expected to return to the stage after convincing myself that chapter was over.</p>
                            <p className="text-ivory">But each began with the same question:</p>
                            <p className="font-serif italic text-2xl md:text-3xl text-gold pt-2">
                                What can I create from what life has taught me?
                            </p>
                        </div>
                    </div>

                    {/* Three pathways */}
                    <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 lg:gap-12">
                        <PathwayCard
                            index="01"
                            title="Valanti Labs"
                            subtitle="Rebuilding confidence through movement"
                            body="During treatment and recovery, I experienced how quickly confidence in your own body can disappear. Valanti Labs is developing intelligent, video-based movement technology designed to help people better understand movement using an ordinary smartphone."
                            quote={QUOTES.bodyWorth}
                            ctaLabel="Explore Valanti Labs"
                            ctaHref="/building/valanti-labs"
                            ctaTestId={TID.home.labsCta}
                            image={IMG.warmShadow}
                            tint="oxblood"
                        />
                        <PathwayCard
                            index="02"
                            title="ALATI"
                            subtitle="Care, tradition and a business built with my son"
                            body="ALATI brings together traditional soap-making, carefully selected ingredients, Greek heritage and the story of a mother and son building something meaningful together."
                            ctaLabel="Discover ALATI"
                            ctaHref="/building/alati"
                            ctaTestId={TID.home.alatiCta}
                            image={IMG.soap}
                            imageAlt="Three handmade ALATI soap bars stacked on stone with rosemary and dried calendula."
                            imagePosition="35% center"
                            tint="dark"
                        />
                        <PathwayCard
                            index="03"
                            title="Music"
                            subtitle="Finding my voice again"
                            body="Music was part of my identity for most of my life. Cancer silenced it for a time. Grief called it back. My return is not about pretending nothing happened."
                            quote={QUOTES.survivalIdentity}
                            ctaLabel="Follow the Music"
                            ctaHref="/building/music"
                            ctaTestId={TID.home.musicCta}
                            image={IMG.microphone}
                            tint="dark"
                        />
                    </div>

                    <div className="mt-20 max-w-3xl">
                        <PullQuoteSupporting label="A note" dark>
                            {QUOTES.ventures}
                        </PullQuoteSupporting>
                    </div>
                </div>
            </section>

            {/* ───────────────────── READER INVITATION ───────────────────── */}
            <section className="bg-ivory text-charcoal">
                <div className="container-editorial py-24 md:py-36">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                        <div className="lg:col-span-6">
                            <p className="eyebrow text-oxblood">Section 06 — An Invitation</p>
                            <h2 className="display-2 mt-6">
                                Perhaps your story is <span className="italic">not finished either.</span>
                            </h2>

                            <div className="mt-10 space-y-5 body-base text-charcoal/80">
                                <p>You do not need to have survived cancer to understand what it feels like to lose part of yourself.</p>
                                <p>You may be rebuilding after illness.</p>
                                <p>You may be grieving.</p>
                                <p>You may be living in a body that feels unfamiliar.</p>
                                <p>You may simply know there is something inside you that you have postponed for too long.</p>
                                <p className="text-charcoal">I cannot tell you what your next beginning should look like.</p>
                                <p className="text-charcoal">I can show you what mine looks like while it is being built.</p>
                            </div>

                            <div className="mt-10 pl-6 border-l-2 border-gold">
                                <p className="font-serif italic text-2xl md:text-[28px] leading-snug text-charcoal">
                                    {QUOTES.transformation}
                                </p>
                            </div>
                        </div>

                        <div className="lg:col-span-6 lg:pl-12 lg:border-l lg:border-charcoal/10">
                            <p className="eyebrow text-stone mb-6">Follow the Journey</p>
                            <p className="font-serif text-2xl md:text-3xl leading-snug mb-10">
                                Receive personal reflections, founder updates, music and behind-the-scenes stories as I
                                continue building from where I am.
                            </p>
                            <EmailSignup source="homepage" />
                        </div>
                    </div>
                </div>
            </section>

            {/* ───────────────────── FINAL STATEMENT (dark) ───────────────────── */}
            <section className="relative bg-charcoal text-ivory overflow-hidden">
                <div className="absolute inset-0 opacity-15">
                    <img src={IMG.stage} alt="" aria-hidden="true" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-b from-charcoal/95 to-charcoal" />
                </div>

                <div className="container-narrow relative py-32 md:py-48 text-center">
                    <h2 className="font-serif text-4xl sm:text-5xl md:text-7xl lg:text-8xl leading-[1.05] tracking-[-0.015em] font-light text-ivory">
                        My life began<br />
                        <span className="italic text-gold/95">when they told me</span><br />
                        it was over.
                    </h2>

                    <div className="mt-14 space-y-4 body-lead text-ivory/70 max-w-xl mx-auto">
                        <p>Not because the fear disappeared.</p>
                        <p>Not because everything became easy.</p>
                        <p className="text-ivory">
                            But because I finally understood that being alive and living are not the same thing.
                        </p>
                        <p>This is the story of what I chose to do next.</p>
                    </div>

                    <div className="mt-16 mx-auto h-px w-16 bg-gold/70" />

                    <p className="mt-16 font-serif italic text-3xl md:text-5xl text-gold">
                        Begin from where you are.
                    </p>

                    <div className="mt-12">
                        <Link to="/journey" data-testid={TID.home.followJourney} className="btn-secondary-light">
                            Follow the Journey <ArrowRight size={16} strokeWidth={1.5} />
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}

function PathwayCard({ index, title, subtitle, body, quote, ctaLabel, ctaHref, ctaTestId, image, imageAlt, imagePosition, tint }) {
    return (
        <article className="group flex flex-col h-full">
            <div className="relative aspect-[4/5] overflow-hidden mb-8">
                <img
                    src={image}
                    alt={imageAlt || ""}
                    aria-hidden={imageAlt ? undefined : "true"}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.04]"
                    style={imagePosition ? { objectPosition: imagePosition } : undefined}
                />
                <div
                    className={`absolute inset-0 ${
                        tint === "oxblood"
                            ? "bg-oxblood/55 mix-blend-multiply"
                            : "bg-gradient-to-t from-charcoal via-charcoal/40 to-transparent"
                    }`}
                />
                <div className="absolute top-5 left-5">
                    <span className="eyebrow text-gold">{index}</span>
                </div>
                <div className="absolute bottom-5 left-5 right-5">
                    <p className="font-serif text-3xl md:text-4xl text-ivory leading-tight">{title}</p>
                </div>
            </div>

            <p className="eyebrow text-gold/80 mb-3">{subtitle}</p>
            <p className="body-base text-ivory/75 mb-6 flex-1">{body}</p>

            {quote && (
                <p className="font-serif italic text-lg text-ivory/85 mb-6 border-l border-gold/70 pl-4">
                    {quote}
                </p>
            )}

            <Link
                to={ctaHref}
                data-testid={ctaTestId}
                className="inline-flex items-center gap-2 eyebrow text-gold hover:text-ivory transition-colors mt-auto"
            >
                {ctaLabel} <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
        </article>
    );
}
